import { useEffect, useMemo, useRef, useState } from 'react';
import { getLogs } from '../services/apiService.ts';
import type { LogEntry } from '../types/logs.ts';

interface UseLogsParams {
  level?: string;
  query?: string;
  from?: string;
  to?: string;
  size?: number;
}

export const useLogs = ({ level = '', query = '', from = '', to = '', size = 50 }: UseLogsParams) => {
  const [rows, setRows] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);
  const pageRef = useRef(0);
  const hasMoreRef = useRef(false);
  const mountedRef = useRef(false);
  const moreFlightRef = useRef<{ generation: number; token: symbol } | null>(null);

  const filters = useMemo(() => ({ level, query, from, to, size }), [level, query, from, to, size]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestSeq.current += 1;
      moreFlightRef.current = null;
    };
  }, []);

  useEffect(() => {
    const generation = ++requestSeq.current;
    moreFlightRef.current = null;
    pageRef.current = 0;
    hasMoreRef.current = false;
    setRows([]);
    setHasMore(false);
    setTotal(0);
    setError(null);
    setLoading(true);

    const timer = window.setTimeout(async () => {
      try {
        const data = await getLogs({ ...filters, page: 0 });
        if (!mountedRef.current || requestSeq.current !== generation) return;
        setRows(data.content);
        pageRef.current = data.number;
        hasMoreRef.current = !data.last;
        setHasMore(!data.last);
        setTotal(data.totalElements);
      } catch (err: any) {
        if (!mountedRef.current || requestSeq.current !== generation) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load logs');
      } finally {
        if (mountedRef.current && requestSeq.current === generation) {
          setLoading(false);
        }
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [filters]);

  const loadMore = async () => {
    if (!mountedRef.current || moreFlightRef.current || !hasMoreRef.current) return;

    const generation = requestSeq.current;
    const flight = { generation, token: Symbol('logs-page-flight') };
    const nextPage = pageRef.current + 1;
    moreFlightRef.current = flight;
    setLoading(true);
    setError(null);

    try {
      const data = await getLogs({ ...filters, page: nextPage });
      if (
        !mountedRef.current
        || requestSeq.current !== generation
        || moreFlightRef.current !== flight
      ) return;

      setRows((prev) => [...prev, ...data.content]);
      pageRef.current = data.number;
      hasMoreRef.current = !data.last;
      setHasMore(!data.last);
      setTotal(data.totalElements);
    } catch (err: any) {
      if (
        !mountedRef.current
        || requestSeq.current !== generation
        || moreFlightRef.current !== flight
      ) return;

      setError(err?.response?.data?.message || err?.message || 'Failed to load more logs');
    } finally {
      if (
        mountedRef.current
        && requestSeq.current === generation
        && moreFlightRef.current === flight
      ) {
        moreFlightRef.current = null;
        setLoading(false);
      }
    }
  };

  return { rows, loading, loadMore, hasMore, total, error };
};
